import { uuid } from 'short-uuid';

import { WIDGET_FILTER_OPTIONS, WIDGET_FILTER_TYPES } from 'common/Widgets/constants';
import { ContractBenefitsTableRow } from 'common/Widgets/ContractBenefits/types';
import { getContractBenefitsTableData } from 'common/Widgets/ContractBenefits/utils';
import { DataItemType } from 'common/Widgets/Graph/types';
import { isCommunityCCRC } from 'components/Community/utils';
import { sortCommunityContracts } from 'components/CommunityInfo/careTypes/utils';
import { AGGREGATION_TYPE, VALUE_DIFFERENCE_DISPLAY, VIEW_CATEGORY } from 'components/View/constants';
import { DataQuarterSelectionType } from 'components/View/filters/DataQuarterFilter/types';
import { FEES_PRICING_TYPES, FEES_PRICING_TYPES_LABELS } from 'constants/careTypes';
import { LEVELS } from 'constants/fees';
import { LIVING_TYPES } from 'constants/livingTypes';
import { CommunityOccupancy, CommunityType } from 'types/communities';
import { CommunityContract, ContractType } from 'types/contracts';
import { OccupancyType } from 'types/occupancies';
import { calculateOccupancy } from 'utils/data/communities';
import { getOccupancyPercentageAggregate } from 'utils/data/occupancies';
import { getById } from 'utils/helpers/idMap';

import {
  CMA_SUMMARY_APARTMENT_TYPE_DATA_ROW,
  CMA_SUMMARY_APARTMENT_TYPE_DATA_ROW_ROOM_COMPARISON,
  CMA_SUMMARY_APARTMENT_TYPE_GROUP_DATA_ROW,
  CMA_SUMMARY_APARTMENT_TYPE_GROUP_DATA_ROW_ROOM_COMPARISON,
  CMA_SUMMARY_CARE_FEE_DATA_ROW,
  CMA_SUMMARY_CARE_FEE_GROUP_DATA_ROW,
  CMA_SUMMARY_COMMUNITY_CONTRACT_ROW,
  CMA_SUMMARY_COMPETITOR_AVERAGE_LABEL,
  CMA_SUMMARY_CONTRACT_BENEFIT_DATA_ROW,
  CMA_SUMMARY_DATA_FEE_INCENTIVE_ROW_TYPE,
  CMA_SUMMARY_DATA_ROW_TYPE,
  CMA_SUMMARY_LIVING_TYPE_DATA_ROW,
  CMA_SUMMARY_LIVING_TYPE_FEE_DATA_ROW,
  CMA_SUMMARY_MEDICATION_FEE_DATA_ROW,
  CMA_SUMMARY_MEDICATION_FEE_GROUP_DATA_ROW,
  CMA_SUMMARY_NON_AVERAGEABLE_ROW_TYPES,
  CMA_SUMMARY_NOT_APPLICABLE_VALUE,
  CMA_SUMMARY_REVIEW_TYPE_DATA_ROW,
  CMA_SUMMARY_ROOM_COMPARISON_DEFAULT_SQFT_RANGE,
  CMA_SUMMARY_ROOM_COMPARISON_TYPE_FILTER_ROW_FIELDS,
  CMA_SUMMARY_ROOM_DATA_FILTER_TYPES,
  CMA_SUMMARY_ROOM_TYPE_FILTER_COMPARISON_OPTIONS,
  CMA_SUMMARY_ROOM_TYPE_GROUPING,
  CONTACT_INFO_FIELDS,
  FEE_ENTRY_TYPES,
  INDEPENDENT_LIVING_TYPE,
} from './constants';
import {
  CMAData,
  CMADataRow,
  CMASummaryFilterField,
  CMASummaryFilters,
  CMASummaryIncentive,
  CMASummaryParsedCommunityData,
  CMASummaryParsedValue,
} from './types';
import { extractNumericValue, isValueObject } from './valueUtils';
import { COMMUNITY_CONTRACT_SELECTION_TYPE, COMMUNITY_CONTRACT_TYPE, CONTRACT_DISPLAY_TYPES } from '../../constants';
import { ApartmentType, ApartmentTypeGroup } from '../../types/accommodations';
import { CommunityBaseIncentiveType } from '../../types/incentives';
import { LivingType } from '../../types/livingType';

// Check CMA values if they are defined. If value is 0, it will be treated as defined.
export const isDefined = <T>(value: T | null | undefined): value is T => value !== null && value !== undefined;

export const CMA_ROW_PADDING_LEVELS = {
  LEVEL_1: 1,
  LEVEL_2: 2,
  LEVEL_3: 3,
} as const;

export const isRowExpandable = (cmaSummaryRowType: string) => {
  return [
    CMA_SUMMARY_APARTMENT_TYPE_GROUP_DATA_ROW,
    CMA_SUMMARY_CARE_FEE_GROUP_DATA_ROW,
    CMA_SUMMARY_MEDICATION_FEE_GROUP_DATA_ROW,
    CMA_SUMMARY_COMMUNITY_CONTRACT_ROW,
  ].includes(cmaSummaryRowType);
};

const addCMALivingTypeRows = ({
  cmaSummaryData,
  livingTypes,
  occupancies,
  livingInfo,
  feesIncentives,
  fees,
  apartmentTypeGroups,
  filters,
  community,
  competitors,
  contracts,
  contractBenefits,
}: {
  cmaSummaryData: any[];
  livingTypes: LivingType[];
  occupancies?: any[][];
  livingInfo?: any[][];
  fees?: any[][];
  feesIncentives?: CMASummaryIncentive[][];
  apartmentTypeGroups?: ApartmentTypeGroup[];
  filters: CMASummaryFilters;
  community: any;
  competitors: any[];
  contractBenefits?: any[];
  contracts: ContractType[];
}) => {
  let communityContracts: CommunityContract[] = [];
  if (filters.contractDisplayType === CONTRACT_DISPLAY_TYPES.MY_CONTRACTS) {
    communityContracts = community?.contracts || [];
  } else if (filters.contractDisplayType === CONTRACT_DISPLAY_TYPES.EVERYONES_CONTRACTS) {
    const competitorContracts = competitors?.flatMap((c) => c.contracts) || [];
    const existingContracts = new Set();
    const distinctCompetitorContracts = [...(community?.contracts || []), ...competitorContracts].filter((contract) => {
      if (existingContracts.has(contract.slug)) {
        return false;
      } else {
        existingContracts.add(contract.slug);
        return true;
      }
    });
    communityContracts = distinctCompetitorContracts;
  }
  if (filters?.communityContractSlugs?.length) {
    communityContracts =
      communityContracts?.filter((c: CommunityContract) =>
        filters.communityContractSlugs?.some((cf) => cf === c.slug)
      ) || [];
  }
  communityContracts = communityContracts.sort(sortCommunityContracts);

  const communitiesIds = [community.id, ...competitors?.flatMap((c) => c.id)];
  const shouldGroupApartmentTypes = filters?.roomTypeGrouping !== CMA_SUMMARY_ROOM_TYPE_GROUPING.DONT_GROUP;
  let filteredLivingTypes = [...livingTypes];
  if (filters?.careTypes?.length) {
    filteredLivingTypes = filteredLivingTypes.filter((livingType) => filters?.careTypes.includes(livingType.id));
  }

  filteredLivingTypes.forEach((livingType) => {
    let livingTypeRowAdded = false;
    for (let contractIndex = 0; contractIndex < communityContracts.length; contractIndex++) {
      const contract = communityContracts[contractIndex];
      let contractTypeRow: any[] = [];
      const temporaryApartmentGroupRows: any[] = [];
      const temporaryCareFeesRows: any[] = [];
      const temporaryMedicationFeeRows: any[] = [];
      const feesRows: any[] = [];
      const livingTypeRow = [];
      const benefitsRow: any[] = [];

      const mostCommonContractsPerCompetitor = (competitors || [])?.map((competitor, competitorIndex) => {
        const mostCommonContract = getMostCommonContract({
          overrideContractId: filters.communityContractSelection?.overrides?.[competitor.id],
          baseContract: contract,
          competitor,
          selectionType: filters.contractSelectionType,
          livingTypeId: livingType.id,
          data: {
            livingInfo: livingInfo ? livingInfo[competitorIndex + 1] : undefined,
            fees: fees ? fees[competitorIndex + 1] : undefined,
            feesIncentives: feesIncentives ? feesIncentives[competitorIndex + 1] : undefined,
          },
        });
        return mostCommonContract;
      });
      const livingInfoGroups = getLivingInfoGroups({
        livingInfo,
        filters,
        contract,
        mostCommonContractsPerCompetitor,
        livingType,
        shouldGroupApartmentTypes,
      });

      // Generating Contract Type row
      if (isCommunityCCRC(community.rental_types) && livingInfo && feesIncentives && fees) {
        contractTypeRow = [
          {
            key: getRowKey({ type: CMA_SUMMARY_COMMUNITY_CONTRACT_ROW, livingType: livingType?.type, contract }),
            title: getCommunityContractLabel(contract),
            subtitle: livingType?.type,
            type: CMA_SUMMARY_COMMUNITY_CONTRACT_ROW,
            level: CMA_ROW_PADDING_LEVELS.LEVEL_1,
          },
        ];
        const communityContainsData = dataBelongsToContract(
          contract,
          {
            livingInfo: livingInfo ? livingInfo[0] : undefined,
            fees: fees ? fees[0] : undefined,
            feesIncentives: feesIncentives ? feesIncentives[0] : undefined,
          },
          livingType.id
        );
        if (communityContainsData) {
          const refund =
            contract.contract_id === COMMUNITY_CONTRACT_TYPE.RENTAL ? undefined : `${contract.refund_percentage}`;
          contractTypeRow.push([contract.name, refund]);
        } else {
          contractTypeRow.push([null]);
        }

        mostCommonContractsPerCompetitor.forEach((c) => {
          const competitorContractValues = [c?.name];
          if (c?.contract_id !== COMMUNITY_CONTRACT_TYPE.RENTAL) {
            competitorContractValues.push(c?.refund_percentage.toString());
          }
          contractTypeRow.push(competitorContractValues);
        });
      }

      // Generating Benefits
      if (isCommunityCCRC(community.rental_types) && filters?.viewOptions?.contractBenefits) {
        const contractBenefitsTableDataRows = getContractBenefitsTableData(
          contracts.filter((c) => c.id === contract.contract_id),
          contractBenefits,
          [community, ...competitors],
          []
        );

        // Contract Benefits data rows
        benefitsRow.push(
          ...(contractBenefitsTableDataRows.slice(1) || []).map((row: ContractBenefitsTableRow) => {
            return [
              {
                key: uuid(),
                title: 'Benefits',
                type: CMA_SUMMARY_CONTRACT_BENEFIT_DATA_ROW,
                level: CMA_ROW_PADDING_LEVELS.LEVEL_2,
                parent: getRowKey({ type: CMA_SUMMARY_COMMUNITY_CONTRACT_ROW, livingType: livingType?.type, contract }),
              },
              ...[community, ...competitors].map((community: any) => {
                const communityMatchingBenefits = row?.rowItems?.find((item) => item?.community_id === community.id);
                const grouped = communityMatchingBenefits?.groupedBenefits ?? {};

                return grouped;
              }),
            ];
          })
        );
      }

      const contractRowKey = isCommunityCCRC(community.rental_types)
        ? getRowKey({ type: CMA_SUMMARY_COMMUNITY_CONTRACT_ROW, livingType: livingType.type, contract })
        : undefined;

      // Generating Community LivingInfo Group rows
      apartmentTypeGroups?.forEach((apartmentTypeGroup) => {
        let hasCurrentLivingInfoGroup = false;

        const apartmentTypeGroupRowKey = shouldGroupApartmentTypes
          ? getRowKey({
              type: CMA_SUMMARY_APARTMENT_TYPE_GROUP_DATA_ROW,
              livingType: livingType.type,
              apartmentGroupType: apartmentTypeGroup.name,
              contract,
            })
          : undefined;
        const apartmentTypeGroupRow: any =
          shouldGroupApartmentTypes && apartmentTypeGroupRowKey
            ? [
                {
                  key: apartmentTypeGroupRowKey,
                  title: apartmentTypeGroup.name,
                  subtitle: livingType.type,
                  type: CMA_SUMMARY_APARTMENT_TYPE_GROUP_DATA_ROW,
                  level: isCommunityCCRC(community.rental_types)
                    ? CMA_ROW_PADDING_LEVELS.LEVEL_2
                    : CMA_ROW_PADDING_LEVELS.LEVEL_1,
                  parent: contractRowKey,
                  contract,
                },
              ]
            : null;
        const groupRows: any[] = [];
        let hasVisibleChildRows = false;
        if (shouldGroupApartmentTypes && apartmentTypeGroupRow) {
          groupRows.push(apartmentTypeGroupRow);
        }

        livingInfoGroups?.forEach((group) => {
          const currentLivingInfoGroup = group.find(
            (item) => item.apartmentTypeGroupId === apartmentTypeGroup.id && item.livingType === livingType.id
          );
          if (currentLivingInfoGroup) {
            hasCurrentLivingInfoGroup = true;
            if (shouldGroupApartmentTypes && apartmentTypeGroupRow) {
              const row = [currentLivingInfoGroup.baseRent];

              const entranceFeeValue =
                isCommunityCCRC(community.rental_types) && livingType.id === LIVING_TYPES.INDEPENDENT_LIVING
                  ? currentLivingInfoGroup.entranceFee
                  : null;
              const squareFootage = filters?.viewOptions?.squareFootage ? currentLivingInfoGroup.squareFootage : null;
              const pricePerSquareFootage = filters?.viewOptions?.pricePerSquareFootage
                ? currentLivingInfoGroup.pricePerSquareFootage
                : null;

              row.push(entranceFeeValue);
              row.push(squareFootage);
              row.push(pricePerSquareFootage);

              apartmentTypeGroupRow.push(row);
            }
          } else if (shouldGroupApartmentTypes && apartmentTypeGroupRow) {
            apartmentTypeGroupRow.push([]);
          }
        });

        const currentLivingInfoItems = (livingInfo ?? [])
          .map((communityLivingInfo: any[], communityIndex: number) => {
            return communityLivingInfo.map((livingInfoItem) => ({
              ...livingInfoItem,
              comparingContract: communityIndex === 0 ? contract : mostCommonContractsPerCompetitor[communityIndex - 1],
            }));
          })
          .flatMap((communityLivingInfo: any[]) => communityLivingInfo)
          .filter(
            (livingInfo: any) =>
              livingInfo.livingTypeId === livingType.id &&
              livingInfo.apartmentTypeGroupId === apartmentTypeGroup.id &&
              livingInfo.contracts?.some((c: CommunityContract) => c.slug === livingInfo.comparingContract?.slug)
          );

        if (
          hasCurrentLivingInfoGroup &&
          currentLivingInfoItems.some((item) => item.apartmentTypeGroupId === apartmentTypeGroup.id)
        ) {
          [...(apartmentTypeGroup?.apartmentTypes ?? [])]
            .sort((apartmentTypeA: ApartmentType, apartmentTypeB: ApartmentType) => {
              const communityLivingInfo = livingInfo?.length ? livingInfo[0] : undefined;
              if (!communityLivingInfo) {
                return 0;
              }

              const selectedCommunityLivingInfoA = communityLivingInfo.find(
                (item) => item.livingType === livingType.type && item.apartmentType === apartmentTypeA.type
              );
              const selectedCommunityLivingInfoB = communityLivingInfo.find(
                (item) => item.livingType === livingType.type && item.apartmentType === apartmentTypeB.type
              );
              if (!selectedCommunityLivingInfoA?.baseRent) return 1;
              if (!selectedCommunityLivingInfoB?.baseRent) return -1;
              return selectedCommunityLivingInfoA.baseRent - selectedCommunityLivingInfoB.baseRent;
            })
            .forEach((apartmentType) => {
              let rowContainsData = false;
              const selectedLivingInfos: (any | null)[] = [];

              let livingDataRowLevel: number;
              if (isCommunityCCRC(community?.rental_types)) {
                livingDataRowLevel = shouldGroupApartmentTypes
                  ? CMA_ROW_PADDING_LEVELS.LEVEL_3
                  : CMA_ROW_PADDING_LEVELS.LEVEL_2;
              } else if (shouldGroupApartmentTypes) {
                livingDataRowLevel = CMA_ROW_PADDING_LEVELS.LEVEL_2;
              } else {
                livingDataRowLevel = CMA_ROW_PADDING_LEVELS.LEVEL_1;
              }
              const livingDataRowParentKey = shouldGroupApartmentTypes ? apartmentTypeGroupRowKey : contractRowKey;

              const livingDataRow: any = [
                {
                  key: uuid(),
                  parent: livingDataRowParentKey,
                  title: apartmentType.type,
                  subtitle: livingType.type,
                  type: CMA_SUMMARY_APARTMENT_TYPE_DATA_ROW,
                  level: livingDataRowLevel,
                },
              ];

              const isSqftComparison =
                filters?.roomTypeFilterComparisonType ===
                CMA_SUMMARY_ROOM_TYPE_FILTER_COMPARISON_OPTIONS.BY_ROOM_TYPE_AND_SQFT;
              const sqftRange = filters?.roomComparisonSqftRange ?? CMA_SUMMARY_ROOM_COMPARISON_DEFAULT_SQFT_RANGE;
              const showClosestBeyondRange = Boolean(filters?.roomComparisonShowClosestBeyondRange);
              const baseCommunityLivingInfo = livingInfo?.length
                ? livingInfo[0]?.find(
                    (item: any) =>
                      item.livingType === livingType.type &&
                      item.apartmentType === apartmentType.type &&
                      item.contracts.some((c: CommunityContract) => c.slug === contract?.slug)
                  )
                : undefined;

              if (isSqftComparison && !baseCommunityLivingInfo) {
                return;
              }

              if (
                baseCommunityLivingInfo?.unitType?.name &&
                baseCommunityLivingInfo.unitType?.community === community.id
              ) {
                livingDataRow[0].titleMeta = `(${baseCommunityLivingInfo.unitType.name})`;
              }

              livingInfo?.forEach((communityLivingInfo, communityIndex) => {
                const contractToCompare =
                  communityIndex === 0 ? contract : mostCommonContractsPerCompetitor[communityIndex - 1];
                let highlightSqft = false;
                let selectedCommunityLivingInfo: any | null = null;

                if (communityIndex === 0) {
                  selectedCommunityLivingInfo = baseCommunityLivingInfo ?? null;
                } else if (isSqftComparison && baseCommunityLivingInfo) {
                  const { livingInfo: closestLivingInfo, highlightSqft: shouldHighlight } = findClosestLivingInfoMatch({
                    competitorLivingInfo: communityLivingInfo,
                    baseLivingInfo: baseCommunityLivingInfo,
                    livingType,
                    contractToCompare,
                    sqftRange,
                    showClosestBeyondRange,
                  });
                  selectedCommunityLivingInfo = closestLivingInfo;
                  highlightSqft = shouldHighlight;
                } else {
                  selectedCommunityLivingInfo = communityLivingInfo.find(
                    (item: any) =>
                      item.livingType === livingType.type &&
                      item.apartmentType === apartmentType.type &&
                      item.contracts.some((c: CommunityContract) => c.slug === contractToCompare?.slug)
                  );
                }

                selectedLivingInfos[communityIndex] = selectedCommunityLivingInfo ?? null;

                if (selectedCommunityLivingInfo) {
                  rowContainsData = true;
                  const row: { value: number | null; difference?: number | null; highlight?: boolean }[] = [
                    { value: selectedCommunityLivingInfo.baseRent ?? null },
                  ];
                  if (
                    selectedCommunityLivingInfo.unitType?.name &&
                    selectedCommunityLivingInfo.unitType?.community === community.id
                  ) {
                    livingDataRow[0].titleMeta = `(${selectedCommunityLivingInfo.unitType.name})`;
                  }
                  const entranceFeeValue =
                    isCommunityCCRC(community?.rental_types) && livingType.id === LIVING_TYPES.INDEPENDENT_LIVING
                      ? selectedCommunityLivingInfo.entranceFee
                      : null;
                  const squareFootage = filters?.viewOptions?.squareFootage
                    ? selectedCommunityLivingInfo.squareFootage
                    : null;
                  const pricePerSquareFootage = filters?.viewOptions?.pricePerSquareFootage
                    ? selectedCommunityLivingInfo.pricePerSquare
                    : null;

                  row.push({ value: entranceFeeValue });
                  row.push({
                    value: squareFootage,
                    highlight: communityIndex > 0 ? highlightSqft : false,
                  });
                  row.push({ value: pricePerSquareFootage });

                  livingDataRow.push(row);
                } else {
                  livingDataRow.push([]);
                }
              });

              if (rowContainsData) {
                const roomComparisonRows = getRoomComparisonApartmentTypeRows({
                  filters,
                  community,
                  competitors,
                  selectedLivingInfos,
                  fees,
                  livingType,
                  parentRowKey: livingDataRow[0].parent,
                  parentLevel: livingDataRow[0].level,
                  contract,
                });

                if (shouldGroupApartmentTypes) {
                  hasVisibleChildRows = true;
                  groupRows.push(livingDataRow);
                  if (roomComparisonRows.length > 0) {
                    groupRows.push(...roomComparisonRows);
                  }
                } else {
                  temporaryApartmentGroupRows.push(livingDataRow);
                  if (roomComparisonRows.length > 0) {
                    temporaryApartmentGroupRows.push(...roomComparisonRows);
                  }
                }
              }
            });

          if (shouldGroupApartmentTypes && livingType.type !== INDEPENDENT_LIVING_TYPE) {
            filters?.roomComparisonRows?.forEach(
              (roomComparisonRow: CMASummaryFilterField[], roomComparisonRowIndex: number) => {
                const title = roomComparisonRow.map((item) => item.name);
                const subtitle = roomComparisonRow.map(
                  (item) =>
                    Object.values(CMA_SUMMARY_ROOM_DATA_FILTER_TYPES).find((e) => e.id === item.fieldType)?.label
                );
                const rowKey = getRowKey({
                  type: CMA_SUMMARY_APARTMENT_TYPE_GROUP_DATA_ROW_ROOM_COMPARISON,
                  livingType: livingType.type,
                  apartmentGroupType: apartmentTypeGroup.name,
                  index: roomComparisonRowIndex,
                  contract,
                });

                const comparisonRow: any[] = [
                  {
                    key: rowKey,
                    title,
                    subtitle,
                    livingType: livingType.type,
                    apartmentGroupType: apartmentTypeGroup.name,
                    type: CMA_SUMMARY_APARTMENT_TYPE_GROUP_DATA_ROW_ROOM_COMPARISON,
                    contract,
                    level: isCommunityCCRC(community?.rental_types)
                      ? CMA_ROW_PADDING_LEVELS.LEVEL_2
                      : CMA_ROW_PADDING_LEVELS.LEVEL_1,
                    parent: contractRowKey,
                  },
                ];

                communitiesIds.forEach((communityId: number, communityIndex: number) => {
                  let fieldTotal = 0;
                  roomComparisonRow.forEach((filterField: CMASummaryFilterField) => {
                    if (!livingInfo) {
                      return;
                    }
                    const communityLivingInfoRates = livingInfo[communityIndex]
                      .filter(
                        (item: any) =>
                          item.livingType === livingType.type &&
                          apartmentTypeGroup.apartmentTypes.find((i: any) => i.type === item.apartmentType)
                      )
                      .map((item: any) => item.baseRent)
                      .filter((item: any) => Boolean(item) && item > 0);
                    const sumOfAllApartmentypeGroupRates = communityLivingInfoRates.reduce(
                      (acc, curr) => acc + (curr || 0),
                      0
                    );
                    if (!communityLivingInfoRates?.length || sumOfAllApartmentypeGroupRates === 0) {
                      return;
                    }

                    if (filterField.name === CMA_SUMMARY_ROOM_COMPARISON_TYPE_FILTER_ROW_FIELDS.RATE) {
                      if (filterField.fieldType === CMA_SUMMARY_ROOM_DATA_FILTER_TYPES.AVG.id) {
                        fieldTotal +=
                          communityLivingInfoRates.reduce((acc, curr) => acc + curr, 0) /
                          communityLivingInfoRates.length;
                      } else if (filterField.fieldType === CMA_SUMMARY_ROOM_DATA_FILTER_TYPES.MIN.id) {
                        fieldTotal += Math.min(...communityLivingInfoRates);
                      } else if (filterField.fieldType === CMA_SUMMARY_ROOM_DATA_FILTER_TYPES.MAX.id) {
                        fieldTotal += Math.max(...communityLivingInfoRates);
                      }
                    } else if (filterField.name === CMA_SUMMARY_ROOM_COMPARISON_TYPE_FILTER_ROW_FIELDS.CARE) {
                      const communityFees = fees
                        ? fees[communityIndex].filter(
                            (item: any) =>
                              item.livingType === livingType.type &&
                              (!item.feeType || item.feeType === FEE_ENTRY_TYPES.CARE_FEES)
                          )
                        : [];
                      if (communityFees.length) {
                        const communityCareFee = communityFees[0];
                        const careFeeValue = getFeeValueForComparison(communityCareFee, filterField.fieldType);
                        if (typeof careFeeValue === 'number') {
                          fieldTotal += careFeeValue;
                        }
                      }
                    } else if (filterField.name === CMA_SUMMARY_ROOM_COMPARISON_TYPE_FILTER_ROW_FIELDS.MEDICATION) {
                      const communityMedicationFees = fees
                        ? fees[communityIndex].filter(
                            (item: any) =>
                              item.livingType === livingType.type && item.feeType === FEE_ENTRY_TYPES.MEDICATION_FEE
                          )
                        : [];
                      if (communityMedicationFees.length) {
                        const medicationFeeValue = getFeeValueForComparison(
                          communityMedicationFees[0],
                          filterField.fieldType
                        );
                        if (typeof medicationFeeValue === 'number') {
                          fieldTotal += medicationFeeValue;
                        }
                      }
                    }
                  });

                  comparisonRow.push([Math.round(fieldTotal)]);
                });

                const apartmentGroupRowIndex = groupRows.findIndex(
                  (row: any) => row[0].subtitle === livingType.type && row[0].title === apartmentTypeGroup.name
                );
                if (apartmentGroupRowIndex >= 0) {
                  const indexToAdd = apartmentGroupRowIndex + roomComparisonRowIndex + 1;
                  groupRows.splice(indexToAdd, 0, comparisonRow);
                }
              }
            );
          }
        }

        if (shouldGroupApartmentTypes && groupRows.length && hasVisibleChildRows) {
          temporaryApartmentGroupRows.push(...groupRows);
        }
      });

      // Generating Community Fees rows
      if (feesIncentives?.length) {
        const livingTypeFeesIncentives = feesIncentives.map(
          (communityFeeIncentives: CMASummaryIncentive[], communityIndex) => {
            const contractToCompare =
              communityIndex === 0 ? contract : mostCommonContractsPerCompetitor[communityIndex - 1];
            return communityFeeIncentives.filter(
              (item: CMASummaryIncentive) =>
                item?.livingTypeId == livingType.id &&
                item.contracts.some((c: CommunityContract) => c.slug === contractToCompare?.slug)
            );
          }
        );

        Object.values(CMA_SUMMARY_DATA_FEE_INCENTIVE_ROW_TYPE).forEach((communityFeeType) => {
          if (!filters?.viewOptions || !filters.viewOptions[communityFeeType.field]) {
            return;
          }
          const communitiesFeeValues = livingTypeFeesIncentives.map((item: CMASummaryIncentive[]) => {
            if (item[0]) {
              const entry = item[0];
              const fieldName: keyof CMASummaryIncentive = communityFeeType.field;
              const isCommunityFee = communityFeeType.id === CMA_SUMMARY_DATA_FEE_INCENTIVE_ROW_TYPE.COMMUNITY_FEE.id;
              const communityFeeEquivalentFactor = isCommunityFee && entry.frequency;
              const value = communityFeeEquivalentFactor
                ? entry.community_fee_equivalent ?? entry.communityFeeEquivalent
                : item[0][fieldName];
              return [value, communityFeeEquivalentFactor];
            } else {
              return [];
            }
          });

          if (communitiesFeeValues.some((entry: any) => entry && typeof entry[0] === 'number')) {
            const individualFeeRow = {
              key: uuid(),
              title: communityFeeType.label,
              subtitle: livingType.type,
              type: CMA_SUMMARY_LIVING_TYPE_FEE_DATA_ROW,
              level: isCommunityCCRC(community.rental_types)
                ? CMA_ROW_PADDING_LEVELS.LEVEL_2
                : CMA_ROW_PADDING_LEVELS.LEVEL_1,
              parent: isCommunityCCRC(community.rental_types)
                ? getRowKey({ type: CMA_SUMMARY_COMMUNITY_CONTRACT_ROW, livingType: livingType.type, contract })
                : null,
            };

            feesRows.push([individualFeeRow, ...communitiesFeeValues]);
          }
        });
      }

      // Generating Care Fees rows
      if (fees?.length) {
        const getLivingTypeFees = (entryType: number) =>
          fees
            ?.map((communityFees: any[], communityIndex) => {
              const contractToCompare =
                communityIndex === 0 ? contract : mostCommonContractsPerCompetitor[communityIndex - 1];
              return communityFees.filter(
                (item: any) =>
                  item?.livingTypeId == livingType.id &&
                  item.contracts.some((c: CommunityContract) => c.slug === contractToCompare?.slug) &&
                  (entryType === FEE_ENTRY_TYPES.CARE_FEES
                    ? !item.feeType || item.feeType === FEE_ENTRY_TYPES.CARE_FEES
                    : item.feeType === FEE_ENTRY_TYPES.MEDICATION_FEE)
              );
            })
            .map((communityFees: any[]) => (communityFees?.length ? communityFees[0] : []));

        const livingTypeCareFees = getLivingTypeFees(FEE_ENTRY_TYPES.CARE_FEES);
        const livingTypeMedicationFees = getLivingTypeFees(FEE_ENTRY_TYPES.MEDICATION_FEE);

        const buildFeeSection = ({
          livingTypeFees,
          title,
          groupRowType,
          dataRowType,
          rowsAccumulator,
          aggregationType,
          showFeeNote = false,
        }: {
          livingTypeFees: any[];
          title: string;
          groupRowType: string;
          dataRowType: string;
          rowsAccumulator: any[];
          aggregationType?: number;
          showFeeNote?: boolean;
        }) => {
          const communityFees = livingTypeFees.map((communityCareFee: any) => {
            if (!communityCareFee) {
              return [];
            }
            let value = undefined;
            const pricingTypeId = communityCareFee?.pricingTypeId;
            const pricingTypeLabel =
              pricingTypeId && FEES_PRICING_TYPES_LABELS[pricingTypeId]
                ? FEES_PRICING_TYPES_LABELS[pricingTypeId]
                : communityCareFee?.pricingType;
            const type = pricingTypeLabel ?? communityCareFee?.pricingType ?? communityCareFee?.pricingTypeId;
            const note = showFeeNote ? communityCareFee?.feeNote : null;
            let subtitle = '';

            const levelValues = [
              communityCareFee?.level1,
              communityCareFee?.level2,
              communityCareFee?.level3,
              communityCareFee?.level4,
              communityCareFee?.level5,
              communityCareFee?.level6,
              communityCareFee?.level7,
              communityCareFee?.level8,
            ];
            const hasLevelsData = levelValues.some((level) => isDefined(level));

            if (communityCareFee.pricingTypeId === FEES_PRICING_TYPES.LEVELS || hasLevelsData) {
              const presentLevels = levelValues.filter((item) => isDefined(item));
              const validLevelValues = levelValues.filter((item) => isDefined(item) && item > 0);
              const levelsCount = presentLevels.length;
              const highestLevelIndex = levelValues.reduce(
                (max, value, i) => (isDefined(value) ? Math.max(max, i + 1) : max),
                0
              );

              const resolvedAggregationType = aggregationType ?? AGGREGATION_TYPE.AVG;
              const averageLevelsValue =
                levelsCount > 0
                  ? presentLevels.reduce((accumulator, current) => accumulator + (current || 0), 0) / levelsCount
                  : undefined;
              const resolvedAverageValue = communityCareFee.averageCareCost ?? averageLevelsValue;

              switch (resolvedAggregationType) {
                case AGGREGATION_TYPE.MAX:
                  value = validLevelValues.length ? Math.max(...validLevelValues) : resolvedAverageValue;
                  break;
                case AGGREGATION_TYPE.MIN:
                  value = validLevelValues.length ? Math.min(...validLevelValues) : resolvedAverageValue;
                  break;
                case AGGREGATION_TYPE.AVG:
                default:
                  {
                    value = resolvedAverageValue;
                  }
                  subtitle = `${highestLevelIndex} ${highestLevelIndex === 1 ? 'Level' : 'Levels'}`;
              }
            } else if (
              [FEES_PRICING_TYPES.A_LA_CARTE, FEES_PRICING_TYPES.POINTS].includes(communityCareFee.pricingTypeId)
            ) {
              if (communityCareFee.averageCareCost) {
                value = communityCareFee.averageCareCost;
                subtitle = FEES_PRICING_TYPES_LABELS[communityCareFee.pricingTypeId];
              }
            }

            return [value, type, subtitle, note];
          });

          const hasFeeRow = communityFees.some(
            (communityCareFee: any) =>
              communityCareFee?.length && (isDefined(communityCareFee[0]) || communityCareFee[1] || communityCareFee[2])
          );
          const hasFeeGroup = livingTypeFees.some((communityCareFee: any) =>
            LEVELS.some((field: string) => isDefined(communityCareFee?.[field]))
          );

          if (!hasFeeRow && !hasFeeGroup) {
            return;
          }

          const groupUUID = getRowKey({
            type: groupRowType,
            livingType: livingType.type,
            contract,
          });
          let feeLevel, feeParent;
          if (isCommunityCCRC(community.rental_types)) {
            feeLevel = CMA_ROW_PADDING_LEVELS.LEVEL_2;
            feeParent = getRowKey({
              type: CMA_SUMMARY_COMMUNITY_CONTRACT_ROW,
              livingType: livingType.type,
              contract,
            });
          } else {
            feeLevel = hasFeeGroup ? CMA_ROW_PADDING_LEVELS.LEVEL_1 : undefined;
            feeParent = undefined;
          }

          if (hasFeeRow) {
            rowsAccumulator.push([
              {
                key: groupUUID,
                title,
                subtitle: livingType.type,
                living_type: livingType.type,
                type: groupRowType,
                level: feeLevel,
                parent: feeParent,
              },
              ...communityFees,
            ]);
          }

          if (hasFeeGroup) {
            const feeLevelsRows = LEVELS.map((field: string, index: number) => {
              const level = index + 1;
              const rowKey = getRowKey({
                type: dataRowType,
                livingType: livingType.type,
                level,
                contract,
              });
              const levelsTitleCell = {
                key: rowKey,
                parent: groupUUID,
                title: `Level ${index + 1}`,
                living_type: livingType.type,
                type: dataRowType,
                level: isCommunityCCRC(community.rental_types)
                  ? CMA_ROW_PADDING_LEVELS.LEVEL_3
                  : CMA_ROW_PADDING_LEVELS.LEVEL_2,
              };

              const levelsValuesCells = livingTypeFees.map((communityCareFee: any) => communityCareFee[field]);
              const hasAnyLevelValues = levelsValuesCells.some((item: any) => isDefined(item));

              return hasAnyLevelValues ? [levelsTitleCell, ...levelsValuesCells] : [];
            });

            const filteredFeeLevelsRows = feeLevelsRows.filter((item: any[]) => item.length);
            rowsAccumulator.push(...filteredFeeLevelsRows);
          }
        };

        buildFeeSection({
          livingTypeFees: livingTypeCareFees,
          title: 'Care Fees',
          groupRowType: CMA_SUMMARY_CARE_FEE_GROUP_DATA_ROW,
          dataRowType: CMA_SUMMARY_CARE_FEE_DATA_ROW,
          rowsAccumulator: temporaryCareFeesRows,
          aggregationType: filters?.careLevelsStatsType,
          showFeeNote: filters?.viewOptions?.feeNote,
        });

        buildFeeSection({
          livingTypeFees: livingTypeMedicationFees,
          title: 'Medication Fee',
          groupRowType: CMA_SUMMARY_MEDICATION_FEE_GROUP_DATA_ROW,
          dataRowType: CMA_SUMMARY_MEDICATION_FEE_DATA_ROW,
          rowsAccumulator: temporaryMedicationFeeRows,
          aggregationType: filters?.medicationLevelsStatsType,
          showFeeNote: filters?.viewOptions?.medicationFeeNote,
        });
      }

      // Generating Living Type header row containing occupancies data
      if (occupancies?.length) {
        const communityOccupancies = occupancies.map((item) => {
          const careTypeOccupancies = item?.filter((occupancy: any) => occupancy.livingType === livingType.type);
          if (careTypeOccupancies?.length) {
            const mappedCareTypeOccupancies = careTypeOccupancies.map((item: OccupancyType) => ({
              id: item.id,
              living_type_id: item.livingTypeId,
              apartment_type_id: item.apartmentTypeId,
              total_apartments_units: item.totalApartmentsUnits,
              free_apartments_units: item.freeApartmentsUnits,
              pct_occupancy: item.occupancyPercentage,
            }));

            const { occupancy, totalUnits, freeUnits, hideUnitsRatio } =
              getOccupancyPercentageAggregate(mappedCareTypeOccupancies);
            return [occupancy, totalUnits, freeUnits, hideUnitsRatio];
          }

          return [];
        });

        // Display Occupancy Row
        const hasOccupanciesData = communityOccupancies.some((item: any[]) => item.length); // Generating Community Occupancies rows
        const occupancyLivingTypeRow = {
          key: uuid(),
          title: livingType.type,
          hasOccupancies: hasOccupanciesData,
          subtitle: hasOccupanciesData ? 'Occupancy' : undefined,
          type: CMA_SUMMARY_LIVING_TYPE_DATA_ROW,
        };

        livingTypeRow.push([occupancyLivingTypeRow, ...communityOccupancies]);
      }

      // Push the LIVING TYPE OCCUPANCY row if it has apartment group rows, and push its children after
      const hasLivingInfo = filters?.viewOptions?.roomType && temporaryApartmentGroupRows.length;
      const hasCareFees = filters?.viewOptions?.careFee && temporaryCareFeesRows.length;
      const hasMedicationFee = filters?.viewOptions?.medicationFee && temporaryMedicationFeeRows.length;
      const contractContainsSomeData = Boolean(hasLivingInfo || hasCareFees || hasMedicationFee || feesRows.length);
      if (contractContainsSomeData) {
        if (!livingTypeRowAdded) {
          // Add LivingType Row only once
          cmaSummaryData.push(...livingTypeRow);
          livingTypeRowAdded = true;
        }

        if (contractTypeRow.length) {
          cmaSummaryData.push(contractTypeRow);
          cmaSummaryData.push(...benefitsRow);
        }
        if (hasLivingInfo) {
          cmaSummaryData.push(...temporaryApartmentGroupRows);
        }
        if (hasCareFees) {
          cmaSummaryData.push(...temporaryCareFeesRows);
        }
        if (hasMedicationFee) {
          cmaSummaryData.push(...temporaryMedicationFeeRows);
        }
        cmaSummaryData.push(...feesRows);
      }
    }
  });
};

const getFeeValueForComparison = (fee: any, fieldType?: number | null) => {
  if (!fee || fieldType == null) {
    return null;
  }

  const levelsList = LEVELS.map((field) => fee?.[field]);
  const hasLevelsData = levelsList.some((level) => isDefined(level));

  if (fee?.pricingTypeId === FEES_PRICING_TYPES.LEVELS || hasLevelsData) {
    if (fieldType === CMA_SUMMARY_ROOM_DATA_FILTER_TYPES.AVG.id) {
      if (fee.averageCareCost && fee.averageCareCost > 0) {
        return fee.averageCareCost;
      }
      const presentLevels = levelsList.filter(
        (item: number | null | undefined) => Boolean(item) && (item as number) > 0
      );
      if (!presentLevels.length) {
        return null;
      }
      const sum = levelsList.reduce((accumulator, current) => accumulator + (current || 0), 0);
      return sum / presentLevels.length;
    }
    if (fieldType === CMA_SUMMARY_ROOM_DATA_FILTER_TYPES.MAX.id) {
      return maxIgnoreNullUndefined(levelsList);
    }
    if (fieldType === CMA_SUMMARY_ROOM_DATA_FILTER_TYPES.MIN.id) {
      return minIgnoreNullUndefined(levelsList);
    }
  } else if (typeof fee?.averageCareCost === 'number') {
    return fee.averageCareCost;
  }

  return null;
};

const maxIgnoreNullUndefined = (args: any[] | null) => {
  if (!args) {
    return 0;
  }
  const filteredArgs = args.filter((v) => v != null);
  return filteredArgs.length > 0 ? Math.max(...filteredArgs) : 0;
};

const minIgnoreNullUndefined = (args: any[] | null) => {
  if (!args) {
    return 0;
  }
  const filteredArgs = args.filter((v) => v != null);
  return filteredArgs.length > 0 ? Math.min(...filteredArgs) : 0;
};

const getCellNumericValue = (value: any) => extractNumericValue(value);

const setCellDifference = (value: any, difference?: number | null) => {
  if (isValueObject(value)) {
    return {
      ...value,
      difference,
    };
  }

  if (Array.isArray(value)) {
    const baseValue = value[0];
    return [baseValue, difference];
  }

  if (typeof value === 'number') {
    return [value, difference];
  }

  return value;
};

type ClosestLivingInfoMatch = {
  livingInfo: any | null;
  highlightSqft: boolean;
};

const findClosestLivingInfoMatch = ({
  competitorLivingInfo,
  baseLivingInfo,
  livingType,
  contractToCompare,
  sqftRange,
  showClosestBeyondRange,
}: {
  competitorLivingInfo: any[];
  baseLivingInfo: any | undefined;
  livingType: any;
  contractToCompare: CommunityContract | null | undefined;
  sqftRange: number;
  showClosestBeyondRange: boolean;
}): ClosestLivingInfoMatch => {
  if (!competitorLivingInfo?.length || !baseLivingInfo) {
    return { livingInfo: null, highlightSqft: false };
  }

  const candidates = competitorLivingInfo.filter(
    (item: any) =>
      item.livingType === livingType.type &&
      item.contracts?.some((contract: CommunityContract) => contract.slug === contractToCompare?.slug)
  );

  if (!candidates.length) {
    return { livingInfo: null, highlightSqft: false };
  }

  const baseGroupId = baseLivingInfo.apartmentTypeGroupId;
  let filteredCandidates = candidates;

  if (baseGroupId) {
    filteredCandidates = candidates.filter((item: any) => item.apartmentTypeGroupId === baseGroupId);
    if (!filteredCandidates.length) {
      return { livingInfo: null, highlightSqft: false };
    }
  } else {
    filteredCandidates = candidates.filter((item: any) => item.apartmentType === baseLivingInfo.apartmentType);
    if (!filteredCandidates.length) {
      return { livingInfo: null, highlightSqft: false };
    }
  }

  const baseSqft = typeof baseLivingInfo.squareFootage === 'number' ? baseLivingInfo.squareFootage : undefined;

  let bestCandidate = filteredCandidates[0];
  if (baseSqft !== undefined) {
    const sqFtCandidates = filteredCandidates.filter((item: any) => typeof item.squareFootage === 'number');
    if (sqFtCandidates.length) {
      bestCandidate = sqFtCandidates.reduce((closest: any, candidate: any) => {
        if (!closest) {
          return candidate;
        }
        const closestDiff = Math.abs((closest.squareFootage ?? 0) - baseSqft);
        const currentDiff = Math.abs((candidate.squareFootage ?? 0) - baseSqft);
        if (currentDiff < closestDiff) {
          return candidate;
        }
        if (currentDiff === closestDiff) {
          return candidate.squareFootage >= baseSqft ? candidate : closest;
        }
        return closest;
      }, sqFtCandidates[0]);
    }
  }

  if (!bestCandidate) {
    return { livingInfo: null, highlightSqft: false };
  }

  const difference =
    baseSqft !== undefined && typeof bestCandidate.squareFootage === 'number'
      ? Math.abs(bestCandidate.squareFootage - baseSqft)
      : null;

  if (difference !== null && difference > sqftRange && !showClosestBeyondRange) {
    return { livingInfo: null, highlightSqft: false };
  }

  return {
    livingInfo: bestCandidate,
    highlightSqft: Boolean(difference !== null && difference > sqftRange),
  };
};

const getRoomComparisonApartmentTypeRows = ({
  filters,
  selectedLivingInfos,
  fees,
  livingType,
  parentRowKey,
  parentLevel,
  community,
  competitors,
  contract,
}: {
  filters: any;
  selectedLivingInfos: (any | null)[];
  fees?: any[][];
  livingType: any;
  parentRowKey?: string;
  parentLevel: number;
  community: any;
  competitors: any[];
  contract: CommunityContract;
}) => {
  const rowsToAdd: any[] = [];
  const baseLivingInfo = selectedLivingInfos?.[0] ?? null;

  if (livingType.type === INDEPENDENT_LIVING_TYPE || !baseLivingInfo) {
    return rowsToAdd;
  }

  filters?.roomComparisonRows?.forEach((roomComparisonRow: CMASummaryFilterField[]) => {
    const roomComparisonRateFieldExists = roomComparisonRow.some(
      (field: CMASummaryFilterField) => field.name === CMA_SUMMARY_ROOM_COMPARISON_TYPE_FILTER_ROW_FIELDS.RATE
    );
    const roomComparisonCareField = roomComparisonRow.find(
      (field: CMASummaryFilterField) => field.name === CMA_SUMMARY_ROOM_COMPARISON_TYPE_FILTER_ROW_FIELDS.CARE
    );
    const roomComparisonMedicationField = roomComparisonRow.find(
      (field: CMASummaryFilterField) => field.name === CMA_SUMMARY_ROOM_COMPARISON_TYPE_FILTER_ROW_FIELDS.MEDICATION
    );

    const title = roomComparisonRow.map((item) => item.name);
    const subtitle = roomComparisonRow.map(
      (item) => Object.values(CMA_SUMMARY_ROOM_DATA_FILTER_TYPES).find((e) => e.id === item.fieldType)?.label
    );
    const comparisonRowKey = getRowKey({
      type: CMA_SUMMARY_APARTMENT_TYPE_DATA_ROW_ROOM_COMPARISON,
      livingType: livingType.type,
      apartmentType: baseLivingInfo.apartmentType,
      roomComparisonFilters: roomComparisonRow,
      contract,
    });
    const comparisonRow: any[] = [
      {
        key: comparisonRowKey,
        parent: parentRowKey,
        livingType: livingType.type,
        apartmentType: baseLivingInfo.apartmentType,
        title,
        subtitle,
        type: CMA_SUMMARY_APARTMENT_TYPE_DATA_ROW_ROOM_COMPARISON,
        level: parentLevel,
      },
    ];

    [community, ...competitors].forEach((_, communityIndex) => {
      const selectedLivingInfo = selectedLivingInfos?.[communityIndex] ?? null;
      const requiresBaseRent = roomComparisonRateFieldExists;
      const hasBaseRent = typeof selectedLivingInfo?.baseRent === 'number' && selectedLivingInfo.baseRent > 0;

      if (!selectedLivingInfo || (requiresBaseRent && !hasBaseRent)) {
        comparisonRow.push([]);
        return;
      }

      let fieldTotal = 0;
      let hasFieldValue = false;

      if (roomComparisonRateFieldExists && hasBaseRent) {
        fieldTotal += selectedLivingInfo.baseRent;
        hasFieldValue = true;
      }

      if (roomComparisonCareField) {
        const careFee =
          fees && fees[communityIndex]
            ? fees[communityIndex].find(
                (item: any) =>
                  item.livingType === livingType.type && (!item.feeType || item.feeType === FEE_ENTRY_TYPES.CARE_FEES)
              )
            : null;
        const careValue = getFeeValueForComparison(careFee, roomComparisonCareField.fieldType);

        if (typeof careValue === 'number') {
          fieldTotal += careValue;
          hasFieldValue = true;
        }
      }

      if (roomComparisonMedicationField) {
        const medicationFee =
          fees && fees[communityIndex]
            ? fees[communityIndex].find(
                (item: any) => item.livingType === livingType.type && item.feeType === FEE_ENTRY_TYPES.MEDICATION_FEE
              )
            : null;
        const medicationValue = getFeeValueForComparison(medicationFee, roomComparisonMedicationField.fieldType);
        if (typeof medicationValue === 'number') {
          fieldTotal += medicationValue;
          hasFieldValue = true;
        }
      }

      if (hasFieldValue) {
        comparisonRow.push([Math.round(fieldTotal)]);
      } else {
        comparisonRow.push([]);
      }
    });

    const hasBaseValue = Array.isArray(comparisonRow[1]) && comparisonRow[1].length > 0;

    if (hasBaseValue && !isSimilarComparisonRowPresent(rowsToAdd, comparisonRow)) {
      rowsToAdd.push(comparisonRow);
    }
  });

  return rowsToAdd;
};

const isSimilarComparisonRowPresent = (existingRows: any[][], rowToAdd: any[]) => {
  const rowToAddMetadata = rowToAdd[0];
  if (!rowToAddMetadata) {
    return false;
  }

  const rowToAddFilters: CMASummaryFilterField[] = rowToAddMetadata.title.map((name: string, index: number) => ({
    name,
    fieldType: rowToAddMetadata.subtitle[index],
  }));

  return existingRows.some((row: any[]) => {
    const currentRowMetadata = row[0];
    if (!currentRowMetadata) {
      return false;
    }
    const currentRowFilters: CMASummaryFilterField[] = currentRowMetadata.title.map((name: string, index: number) => ({
      name,
      fieldType: currentRowMetadata.subtitle[index],
    }));

    if (currentRowFilters.length !== rowToAddFilters.length) {
      return false;
    }

    return rowToAddFilters.every((rowToAddFilter: CMASummaryFilterField) => {
      const matchingFilter = currentRowFilters.find((currentRowFilter: CMASummaryFilterField) => {
        if (currentRowFilter.name !== rowToAddFilter.name) {
          return false;
        }
        if (
          currentRowFilter.name === CMA_SUMMARY_ROOM_COMPARISON_TYPE_FILTER_ROW_FIELDS.CARE &&
          currentRowFilter.fieldType !== rowToAddFilter.fieldType
        ) {
          return false;
        }

        return true;
      });
      return Boolean(matchingFilter);
    });
  });
};

const getFilteredApartmentTypeGroups = ({
  apartmentTypeGroups,
  filters,
}: {
  apartmentTypeGroups: ApartmentTypeGroup[];
  filters: any;
}) => {
  return apartmentTypeGroups.filter((apartmentGroup) =>
    apartmentGroup.apartmentTypes.find((apartmentType: ApartmentType) => filters?.roomTypes.includes(apartmentType.id))
  );
};

// Builds living info aggregates per community/contract, applying the room-type filters.
// When grouping with the sq.ft comparison is active, it also removes competitor rows
// that lack a matching base-community room inside the allowed sq.ft range.
const getLivingInfoGroups = ({
  livingInfo,
  filters,
  contract,
  mostCommonContractsPerCompetitor,
  livingType,
  shouldGroupApartmentTypes,
}: {
  livingInfo?: any[][];
  filters: any;
  contract: CommunityContract;
  mostCommonContractsPerCompetitor: (CommunityContract | null | undefined)[];
  livingType: LivingType;
  shouldGroupApartmentTypes: boolean;
}) => {
  if (!livingInfo) {
    return [];
  }
  if (!filters?.viewOptions?.roomType) {
    return livingInfo;
  }

  const isSqftComparison =
    filters?.roomTypeFilterComparisonType === CMA_SUMMARY_ROOM_TYPE_FILTER_COMPARISON_OPTIONS.BY_ROOM_TYPE_AND_SQFT;
  const shouldApplySqftFiltering =
    shouldGroupApartmentTypes && isSqftComparison && livingType?.type !== INDEPENDENT_LIVING_TYPE;
  let allowedApartmentTypeIdsByGroup: Map<string, Set<number>> | undefined;

  if (isSqftComparison && livingInfo?.length) {
    const baseCommunityLivingInfo = livingInfo[0] ?? [];
    allowedApartmentTypeIdsByGroup = new Map();

    baseCommunityLivingInfo.forEach((communityLivingInfoItem: any) => {
      const matchesContract = communityLivingInfoItem?.contracts?.some(
        (c: CommunityContract) => c.slug === contract?.slug
      );
      const hasApartmentTypeId = communityLivingInfoItem?.apartmentTypeId != null;

      if (!matchesContract || !hasApartmentTypeId) {
        return;
      }

      const key = `${communityLivingInfoItem.apartmentTypeGroupId}-${communityLivingInfoItem.livingTypeId}`;
      if (!allowedApartmentTypeIdsByGroup?.has(key)) {
        allowedApartmentTypeIdsByGroup?.set(key, new Set());
      }
      allowedApartmentTypeIdsByGroup?.get(key)?.add(communityLivingInfoItem.apartmentTypeId);
    });
  }

  const visibleCommunityIndexes = new Set<number>();
  if (!shouldApplySqftFiltering) {
    livingInfo?.forEach((_, index) => visibleCommunityIndexes.add(index));
  } else {
    visibleCommunityIndexes.add(0);
    const sqftRange = filters?.roomComparisonSqftRange ?? CMA_SUMMARY_ROOM_COMPARISON_DEFAULT_SQFT_RANGE;
    const showClosestBeyondRange = Boolean(filters?.roomComparisonShowClosestBeyondRange);
    const baseContractLivingInfo = (livingInfo?.[0] ?? []).filter(
      (item: any) =>
        item.livingType === livingType?.type &&
        item.contracts?.some((c: CommunityContract) => c.slug === contract?.slug)
    );

    baseContractLivingInfo.forEach((baseLivingInfoItem: any) => {
      livingInfo?.forEach((communityLivingInfo, communityIndex) => {
        if (communityIndex === 0) {
          return;
        }
        const contractToCompare = mostCommonContractsPerCompetitor[communityIndex - 1];
        const { livingInfo: closestLivingInfo } = findClosestLivingInfoMatch({
          competitorLivingInfo: communityLivingInfo,
          baseLivingInfo: baseLivingInfoItem,
          livingType,
          contractToCompare,
          sqftRange,
          showClosestBeyondRange,
        });
        if (closestLivingInfo) {
          visibleCommunityIndexes.add(communityIndex);
        }
      });
    });
  }

  const aggregateValues = (values: number[], aggregationType: number) => {
    const filteredValues = values.filter((value) => value !== null && value !== undefined);

    if (filteredValues.length === 0) {
      return null;
    }

    switch (aggregationType) {
      case AGGREGATION_TYPE.MIN:
        return Math.min(...filteredValues);
      case AGGREGATION_TYPE.MAX:
        return Math.max(...filteredValues);
      case AGGREGATION_TYPE.AVG:
      default: {
        const sum = filteredValues.reduce((acc, val) => acc + val, 0);
        return Math.round(sum / filteredValues.length);
      }
    }
  };

  const calculateAggregates = (communityLivingInfo: any[]) => {
    const groupMap = new Map();

    for (const communityLivingInfoItem of communityLivingInfo) {
      const key = `${communityLivingInfoItem.apartmentTypeGroupId}-${communityLivingInfoItem.livingTypeId}-${contract.slug}`;
      if (!groupMap.has(key)) {
        groupMap.set(key, {
          apartmentTypeGroupId: communityLivingInfoItem.apartmentTypeGroupId,
          livingType: communityLivingInfoItem.livingTypeId,
          baseRentValues: [],
          squareFootageValues: [],
          entranceFeeValues: [],
          pricePerSquareFootageValues: [],
        });
      }
      const group = groupMap.get(key);
      group.baseRentValues.push(communityLivingInfoItem.baseRent);
      group.squareFootageValues.push(communityLivingInfoItem.squareFootage);
      group.entranceFeeValues.push(communityLivingInfoItem.entranceFee);
      group.pricePerSquareFootageValues.push(communityLivingInfoItem.pricePerSquare);
    }

    const roomTypeAggregationType = filters?.roomTypesPriceDisplay ?? AGGREGATION_TYPE.AVG;

    return Array.from(groupMap.values()).map((group) => ({
      apartmentTypeGroupId: group.apartmentTypeGroupId,
      baseRent: aggregateValues(group.baseRentValues, roomTypeAggregationType),
      squareFootage: aggregateValues(group.squareFootageValues, roomTypeAggregationType),
      entranceFee: aggregateValues(group.entranceFeeValues, roomTypeAggregationType),
      pricePerSquareFootage: aggregateValues(group.pricePerSquareFootageValues, roomTypeAggregationType),
      livingType: group.livingType,
    }));
  };

  return livingInfo.map((communityLivingInfo, communityIndex) => {
    if (shouldApplySqftFiltering && communityIndex > 0 && !visibleCommunityIndexes.has(communityIndex)) {
      // Exclude group aggregates for competitors that were filtered out by sq ft.
      // If i.e. One Bedroom in competitor A was excluded by sq ft, then it should not
      // be aggregated into the ApartmentTypeGroupRow.
      return [];
    }

    const contractToCompare = communityIndex === 0 ? contract : mostCommonContractsPerCompetitor[communityIndex - 1];
    const contractLivingInfo = communityLivingInfo
      .filter((communityLivingInfo) =>
        communityLivingInfo.contracts.some((c: CommunityContract) => c.slug === contractToCompare?.slug)
      )
      .filter((communityLivingInfoItem) => {
        if (!isSqftComparison) {
          return true;
        }

        const key = `${communityLivingInfoItem.apartmentTypeGroupId}-${communityLivingInfoItem.livingTypeId}`;
        const allowedApartmentTypes = allowedApartmentTypeIdsByGroup?.get(key);
        if (!allowedApartmentTypes || communityLivingInfoItem?.apartmentTypeId == null) {
          return false;
        }

        return allowedApartmentTypes.has(communityLivingInfoItem.apartmentTypeId);
      });
    return calculateAggregates(contractLivingInfo);
  });
};

const getFilteredLivingInfo = ({ livingInfo, filters }: { livingInfo?: any[][]; filters: any }) => {
  if (!livingInfo) {
    return [];
  }
  if (!filters || !filters?.roomTypes || filters?.roomTypes.length === 0) {
    return livingInfo;
  }

  return livingInfo.map((row) =>
    row.filter((livingInfoItem) => filters?.roomTypes.includes(livingInfoItem.apartmentTypeId))
  );
};

export const getCMASummaryReviewSources = (filters: CMASummaryFilters) => {
  const availableReviewSources = WIDGET_FILTER_OPTIONS[WIDGET_FILTER_TYPES.REVIEW_SOURCE];
  return filters?.reviewSources?.length &&
    filters?.reviewSources.length > 0 &&
    filters?.reviewSources.length < availableReviewSources.length
    ? filters?.reviewSources
        .map((value: number) => {
          const source = availableReviewSources.find((reviewSource) => reviewSource.id === value);
          return source?.name;
        })
        .join(', ')
    : 'All Sources';
};

type CMASummaryDataProps = {
  community: any;
  competitors: any[];
  ratings?: DataItemType[];
  livingTypes: LivingType[];
  feesIncentives?: CMASummaryIncentive[][];
  fees?: any[][];
  occupancies?: any[][];
  livingInfo?: any[][];
  incentives?: CommunityBaseIncentiveType[][];
  apartmentTypeGroups: ApartmentTypeGroup[];
  livingInfoGroups?: any[][];
  contractBenefits?: any[];
  contracts: ContractType[];
  filters: CMASummaryFilters;
  snapshotMetaByCommunity?: Record<number, { date: string | null; quarterSlug: string | null }>;
  hasSnapshotOverrides?: boolean;
  dataQuarterSelection?: DataQuarterSelectionType | null;
};

export const getCMASummaryData = ({
  community,
  competitors,
  ratings,
  livingTypes,
  feesIncentives,
  incentives,
  fees,
  occupancies,
  livingInfo,
  apartmentTypeGroups,
  contractBenefits,
  contracts,
  filters,
  snapshotMetaByCommunity,
  hasSnapshotOverrides,
  dataQuarterSelection,
}: CMASummaryDataProps) => {
  if (!community || !competitors) {
    return undefined;
  }

  let cmaSummaryData: CMAData = [[{ key: uuid(), title: '', type: CMA_SUMMARY_DATA_ROW_TYPE.COMMUNITY_NAME }]];

  if (filters?.viewOptions?.contactInformation) {
    const v: CMASummaryFilters['viewOptions'] = filters.viewOptions;

    const rowsToAdd = CONTACT_INFO_FIELDS.filter(({ key }) => v?.[key]).map(
      ({ title, type }) => [{ key: uuid(), title, type }] as CMADataRow
    );
    if (v.yearOpened && (community?.year_opened || competitors?.some((c) => c?.year_opened))) {
      rowsToAdd.push([{ key: uuid(), title: 'Year Opened', type: CMA_SUMMARY_DATA_ROW_TYPE.YEAR_OPENED }]);
    }
    if (v.communityNotes && (community?.notes || competitors?.some((c: any) => c?.notes))) {
      rowsToAdd.push([{ key: uuid(), title: 'Community Notes', type: CMA_SUMMARY_DATA_ROW_TYPE.NOTES }]);
    }

    cmaSummaryData.push(...rowsToAdd);
  }

  if (filters?.viewOptions?.shopDate) {
    cmaSummaryData.push([{ key: uuid(), title: 'Shop Date', type: CMA_SUMMARY_DATA_ROW_TYPE.SHOP_DATE }]);
  }

  if (filters?.viewOptions?.reviews) {
    cmaSummaryData.push([
      {
        key: CMA_SUMMARY_REVIEW_TYPE_DATA_ROW,
        title: 'Reviews',
        subtitle: [getCMASummaryReviewSources(filters)],
        type: CMA_SUMMARY_DATA_ROW_TYPE.REVIEWS,
      },
    ]);
  }

  if (filters?.viewOptions?.careTypes) {
    cmaSummaryData.push([{ key: uuid(), title: 'Care Types', type: CMA_SUMMARY_DATA_ROW_TYPE.CARE_TYPES }]);
  }

  if (isCommunityCCRC(community?.rental_types)) {
    cmaSummaryData.push([{ key: uuid(), title: 'Contract Type', type: CMA_SUMMARY_DATA_ROW_TYPE.CONTRACT_TYPE }]);
  }
  if (filters?.viewOptions?.occupancy) {
    cmaSummaryData.push([
      { key: uuid(), title: 'Occupancy', subtitle: 'Overall', type: CMA_SUMMARY_DATA_ROW_TYPE.OCCUPANCY },
    ]);
  }

  if (filters?.viewOptions?.ratesFees) {
    const filteredApartmentTypeGroups = getFilteredApartmentTypeGroups({ apartmentTypeGroups, filters });
    const filteredLivingInfo = getFilteredLivingInfo({ livingInfo, filters });
    addCMALivingTypeRows({
      cmaSummaryData,
      livingTypes,
      occupancies,
      livingInfo: filters?.roomTypes?.length ? filteredLivingInfo : livingInfo,
      fees,
      feesIncentives,
      apartmentTypeGroups: filters?.roomTypes?.length ? filteredApartmentTypeGroups : apartmentTypeGroups,
      filters,
      community,
      competitors,
      contracts,
      contractBenefits,
    });
  }

  if (
    incentives?.some((item: CommunityBaseIncentiveType[]) => Boolean(item?.length)) &&
    filters?.viewOptions?.incentives
  ) {
    cmaSummaryData.push([
      {
        key: uuid(),
        title: 'Incentives',
        subtitle: 'View community page for more',
        type: CMA_SUMMARY_DATA_ROW_TYPE.INCENTIVES,
      },
    ]);
  }

  const includePriorCompetitors = Boolean(dataQuarterSelection?.includePriorCompetitors);
  const selectedQuarterSlug = dataQuarterSelection?.selectedQuarterSlug ?? null;

  cmaSummaryData.forEach((dataRow) => {
    const rowMetadata = dataRow[0];

    switch (rowMetadata?.type) {
      case CMA_SUMMARY_DATA_ROW_TYPE.COMMUNITY_NAME: {
        dataRow.push(community.comunity_name);
        competitors.forEach((competitor: any) => {
          dataRow.push(competitor.comunity_name);
        });
        break;
      }
      case CMA_SUMMARY_DATA_ROW_TYPE.ADDRESS: {
        dataRow.push(community.address);
        competitors.forEach((competitor: any) => {
          dataRow.push(competitor.address);
        });
        break;
      }
      case CMA_SUMMARY_DATA_ROW_TYPE.DISTANCE: {
        dataRow.push(community.distance);
        competitors.forEach((competitor: any) => {
          dataRow.push(competitor.distance);
        });
        break;
      }
      case CMA_SUMMARY_DATA_ROW_TYPE.WEBSITE: {
        dataRow.push(community.website);
        competitors.forEach((competitor: any) => {
          dataRow.push(competitor.website);
        });
        break;
      }
      case CMA_SUMMARY_DATA_ROW_TYPE.PHONE: {
        dataRow.push(community.phone_number);
        competitors.forEach((competitor: any) => {
          dataRow.push(competitor.phone_number);
        });
        break;
      }
      case CMA_SUMMARY_DATA_ROW_TYPE.YEAR_OPENED: {
        dataRow.push(community.year_opened);
        competitors.forEach((competitor: any) => {
          dataRow.push(competitor.year_opened);
        });
        break;
      }
      case CMA_SUMMARY_DATA_ROW_TYPE.NOTES: {
        dataRow.push(community.notes);
        competitors.forEach((competitor: any) => {
          dataRow.push(competitor.notes);
        });
        break;
      }
      case CMA_SUMMARY_DATA_ROW_TYPE.SHOP_DATE: {
        const resolveShopDate = (communityId: number, fallback?: string | null) => {
          if (hasSnapshotOverrides && snapshotMetaByCommunity) {
            const meta = getById(snapshotMetaByCommunity, communityId);
            if (!meta?.date) {
              return null;
            }

            if (communityId === community.id) {
              return meta.date;
            }

            if (includePriorCompetitors) {
              return meta.date;
            }

            if (!selectedQuarterSlug || meta.quarterSlug === selectedQuarterSlug) {
              return meta.date;
            }

            return null;
          }
          return fallback ?? null;
        };

        dataRow.push(resolveShopDate(community.id, community.latest_snapshot_date));
        competitors.forEach((competitor: any) => {
          dataRow.push(resolveShopDate(competitor.id, competitor.latest_snapshot_date));
        });

        break;
      }
      case CMA_SUMMARY_DATA_ROW_TYPE.REVIEWS: {
        if (!ratings?.length) break;

        const subtitles: string[] = [];

        ratings.forEach((rating) => {
          rating?.sources?.forEach((source: any) => {
            const sourceName = WIDGET_FILTER_OPTIONS[WIDGET_FILTER_TYPES.REVIEW_SOURCE].find(
              (s) => s.id === source.source
            )?.name;

            if (sourceName && !subtitles.includes(sourceName)) {
              subtitles.push(sourceName);
            }
          });
        });

        ratings.forEach((rating) => {
          const row: (string | number | undefined)[] = [];

          row.push(rating?.value?.toFixed(1));
          row.push(rating?.total);

          const sourceMap = new Map<string, any>();
          rating?.sources?.forEach((source: any) => {
            const sourceName = WIDGET_FILTER_OPTIONS[WIDGET_FILTER_TYPES.REVIEW_SOURCE].find(
              (s) => s.id === source.source
            )?.name;

            if (sourceName) {
              sourceMap.set(
                sourceName,
                `${source?.value?.toFixed(1)} · ${source?.total},${sourceName.toLocaleLowerCase().replace(' ', '-')}`
              );
            }
          });

          subtitles.forEach((subtitle) => {
            row.push(sourceMap.get(subtitle));
          });

          dataRow.push(row);
        });

        const existingSubtitles = Array.isArray(dataRow[0]?.subtitle) ? dataRow[0].subtitle : [];

        dataRow[0].subtitle = [...existingSubtitles, ...subtitles];
        break;
      }

      case CMA_SUMMARY_DATA_ROW_TYPE.INCENTIVES: {
        if (incentives) {
          dataRow.push(...incentives);
        }
        break;
      }
      case CMA_SUMMARY_DATA_ROW_TYPE.CARE_TYPES: {
        if (livingInfo && fees && feesIncentives) {
          const livingInfoGroups = Array.isArray(livingInfo) ? livingInfo : [];
          const feesGroups = Array.isArray(fees) ? fees : [];
          const feesIncentivesGroups = Array.isArray(feesIncentives) ? feesIncentives : [];

          const groupCount = Math.max(livingInfoGroups.length, feesGroups.length, feesIncentivesGroups.length);

          for (let i = 0; i < groupCount; i++) {
            const combinedGroup = [
              ...(Array.isArray(livingInfoGroups[i]) ? livingInfoGroups[i] : []),
              ...(Array.isArray(feesGroups[i]) ? feesGroups[i] : []),
              ...(Array.isArray(feesIncentivesGroups[i]) ? feesIncentivesGroups[i] : []),
            ];

            const livingTypeMap = new Map<number, string>();
            for (const item of combinedGroup) {
              if (item && typeof item.livingTypeId === 'number' && typeof item.livingType === 'string') {
                livingTypeMap.set(item.livingTypeId, item.livingType);
              }
            }

            const orderedLivingTypes = Array.from(livingTypeMap.entries())
              .sort((a, b) => a[0] - b[0])
              .map(([, livingType]) => livingType);

            dataRow.push(orderedLivingTypes);
          }
        }
        break;
      }
      case CMA_SUMMARY_DATA_ROW_TYPE.CONTRACT_TYPE: {
        dataRow.push(community.contracts);
        competitors.forEach((competitor: any) => {
          dataRow.push(competitor.contracts);
        });
        break;
      }
      case CMA_SUMMARY_DATA_ROW_TYPE.OCCUPANCY: {
        if (occupancies) {
          const occupancyGroups = Array.isArray(occupancies) ? occupancies : [];

          occupancyGroups.forEach((group) => {
            if (Array.isArray(group)) {
              dataRow.push(group);
            }
          });
        }

        break;
      }
    }
  });

  cmaSummaryData = addCompetitorAverageColumn(cmaSummaryData, filters);
  cmaSummaryData = addDataDifferenceValues(cmaSummaryData, filters);

  return cmaSummaryData;
};

export const prepareCMASummaryDataRowsForAntdTable = (expandedRowKeys: string[], cmaSummaryData?: any[]) => {
  const prepareCMASummaryDataRowForAntdTable = (cmaSummaryDataRow: any) => {
    const rowMetadata = cmaSummaryDataRow[0];
    return {
      key: rowMetadata.key,
      rowContent: cmaSummaryDataRow,
    };
  };

  return cmaSummaryData
    ?.map((row) => prepareCMASummaryDataRowForAntdTable(row))
    .filter((row) => {
      const { rowContent } = row;

      const rowMetadata = rowContent.length ? rowContent[0] : undefined;
      const parentUUID = rowMetadata?.parent;

      return !parentUUID ? true : parentUUID && expandedRowKeys.includes(parentUUID);
    });
};

const averageNumbers = (numbers: number[]) => {
  if (!numbers.length) {
    return null;
  }

  return numbers.reduce((sum, number) => sum + number, 0) / numbers.length;
};

const getAverageValueFromSamples = (values: any[], sampleValue?: any) => {
  const numericValues = values
    .map((value) => extractNumericValue(value))
    .filter((value): value is number => value !== null && value !== undefined);
  const averageValue = averageNumbers(numericValues);

  if (averageValue === null) {
    return null;
  }

  return isValueObject(sampleValue) ? { value: averageValue } : averageValue;
};

const getAverageArrayValue = (values: any[]) => {
  const arrayValues = values.filter(Array.isArray);
  const maxLength = Math.max(0, ...arrayValues.map((value) => value.length));

  if (!maxLength) {
    return CMA_SUMMARY_NOT_APPLICABLE_VALUE;
  }

  const averageValue = Array.from({ length: maxLength }, (_, index) => {
    const indexValues = arrayValues.map((value) => value[index]);
    const sampleValue = indexValues.find((value) => value !== null && value !== undefined);
    return getAverageValueFromSamples(indexValues, sampleValue);
  });

  return averageValue.some((value) => value !== null && value !== undefined)
    ? averageValue
    : CMA_SUMMARY_NOT_APPLICABLE_VALUE;
};

const getAverageOccupancyValue = (values: any[]) => {
  const occupancyValues = values
    .filter((value): value is CommunityOccupancy[] => Array.isArray(value) && value.length > 0)
    .map((value) => {
      const { occupancyPercentage } = calculateOccupancy(value);
      return extractNumericValue(occupancyPercentage);
    })
    .filter((value): value is number => value !== null && value !== undefined);
  return averageNumbers(occupancyValues) ?? CMA_SUMMARY_NOT_APPLICABLE_VALUE;
};

const roundAverageDisplayValue = (value: any) => {
  if (isValueObject(value)) {
    return typeof value.value === 'number' ? { ...value, value: Math.round(value.value) } : value;
  }

  return typeof value === 'number' ? Math.round(value) : value;
};

const roundAverageArrayDisplayValue = (value: any, indexes?: number[]) => {
  if (!Array.isArray(value)) {
    return roundAverageDisplayValue(value);
  }

  return value.map((item, index) =>
    indexes === undefined || indexes.includes(index) ? roundAverageDisplayValue(item) : item
  );
};

const getAverageLivingTypeOccupancyValue = (values: any[]) => {
  const averageValue = getAverageArrayValue(values);

  if (!Array.isArray(averageValue)) {
    return averageValue;
  }

  return averageValue.map((value, index) => {
    if (typeof value !== 'number') {
      return value;
    }

    return index === 1 || index === 2 ? Math.round(value) : value;
  });
};

const getAverageFeeGroupValue = (values: any[]) => {
  const averageValue = getAverageValueFromSamples(values.map((value) => (Array.isArray(value) ? value[0] : value)));

  return averageValue === null || averageValue === undefined
    ? CMA_SUMMARY_NOT_APPLICABLE_VALUE
    : [roundAverageDisplayValue(averageValue), CMA_SUMMARY_NOT_APPLICABLE_VALUE, null, null];
};

const getAverageFeeValue = (values: any[]) => {
  const averageValue = getAverageValueFromSamples(values);

  return averageValue === null || averageValue === undefined
    ? CMA_SUMMARY_NOT_APPLICABLE_VALUE
    : roundAverageDisplayValue(averageValue);
};

const getAverageLivingInfoValue = (values: any[]) =>
  roundAverageArrayDisplayValue(getAverageArrayValue(values), [0, 1]);

const getAverageLivingTypeFeeValue = (values: any[]) =>
  roundAverageArrayDisplayValue(getAverageArrayValue(values), [0]);

const getAverageRoomComparisonValue = (values: any[]) => {
  const averageValue = getAverageValueFromSamples(values);

  return averageValue === null || averageValue === undefined
    ? CMA_SUMMARY_NOT_APPLICABLE_VALUE
    : roundAverageDisplayValue(averageValue);
};

const parseReviewSourceValue = (value: any) => {
  if (typeof value !== 'string') {
    return null;
  }

  const [displayValue, source] = value.split(',');
  const [rating, total] = displayValue.match(/-?\d+(\.\d+)?/g)?.map(Number) ?? [];

  if (rating === undefined || total === undefined || !source) {
    return null;
  }

  return {
    rating,
    total,
    source: source.trim(),
  };
};

const getAverageReviewSourceValue = (values: any[]) => {
  const parsedValues = values
    .map(parseReviewSourceValue)
    .filter((value): value is NonNullable<ReturnType<typeof parseReviewSourceValue>> => Boolean(value));

  const averageRating = averageNumbers(parsedValues.map((value) => value.rating));
  const averageTotal = averageNumbers(parsedValues.map((value) => value.total));
  const source = parsedValues.find((value) => value.source)?.source;

  if (averageRating === null || averageTotal === null || !source) {
    return null;
  }

  return `${averageRating.toFixed(1)} \u00b7 ${Math.round(averageTotal)},${source}`;
};

const getAverageReviewsValue = (values: any[]) => {
  const arrayValues = values.filter(Array.isArray);
  const maxLength = Math.max(0, ...arrayValues.map((value) => value.length));

  if (!maxLength) {
    return CMA_SUMMARY_NOT_APPLICABLE_VALUE;
  }

  const averageValue = Array.from({ length: maxLength }, (_, index) => {
    const indexValues = arrayValues.map((value) => value[index]);

    if (index > 1) {
      return getAverageReviewSourceValue(indexValues);
    }

    const averageValue = getAverageValueFromSamples(indexValues);

    if (typeof averageValue !== 'number') {
      return averageValue;
    }

    return index === 0 ? Number(averageValue.toFixed(1)) : Math.round(averageValue);
  });

  return averageValue.some((value) => value !== null && value !== undefined)
    ? averageValue
    : CMA_SUMMARY_NOT_APPLICABLE_VALUE;
};

const getCompetitorAverageValue = (rowMetadata: any, competitorValues: any[]) => {
  if (rowMetadata?.type === CMA_SUMMARY_DATA_ROW_TYPE.COMMUNITY_NAME) {
    return CMA_SUMMARY_COMPETITOR_AVERAGE_LABEL;
  }

  if (CMA_SUMMARY_NON_AVERAGEABLE_ROW_TYPES.includes(rowMetadata?.type)) {
    return CMA_SUMMARY_NOT_APPLICABLE_VALUE;
  }

  if (rowMetadata?.type === CMA_SUMMARY_DATA_ROW_TYPE.OCCUPANCY) {
    return getAverageOccupancyValue(competitorValues);
  }

  if (rowMetadata?.type === CMA_SUMMARY_LIVING_TYPE_DATA_ROW) {
    return getAverageLivingTypeOccupancyValue(competitorValues);
  }

  if (rowMetadata?.type === CMA_SUMMARY_DATA_ROW_TYPE.REVIEWS) {
    return getAverageReviewsValue(competitorValues);
  }

  if (
    rowMetadata?.type === CMA_SUMMARY_APARTMENT_TYPE_GROUP_DATA_ROW ||
    rowMetadata?.type === CMA_SUMMARY_APARTMENT_TYPE_DATA_ROW
  ) {
    return getAverageLivingInfoValue(competitorValues);
  }

  if (rowMetadata?.type === CMA_SUMMARY_LIVING_TYPE_FEE_DATA_ROW) {
    return getAverageLivingTypeFeeValue(competitorValues);
  }

  if (
    rowMetadata?.type === CMA_SUMMARY_APARTMENT_TYPE_GROUP_DATA_ROW_ROOM_COMPARISON ||
    rowMetadata?.type === CMA_SUMMARY_APARTMENT_TYPE_DATA_ROW_ROOM_COMPARISON
  ) {
    return getAverageRoomComparisonValue(competitorValues);
  }

  if (
    rowMetadata?.type === CMA_SUMMARY_CARE_FEE_GROUP_DATA_ROW ||
    rowMetadata?.type === CMA_SUMMARY_MEDICATION_FEE_GROUP_DATA_ROW
  ) {
    return getAverageFeeGroupValue(competitorValues);
  }

  if (
    rowMetadata?.type === CMA_SUMMARY_CARE_FEE_DATA_ROW ||
    rowMetadata?.type === CMA_SUMMARY_MEDICATION_FEE_DATA_ROW
  ) {
    return getAverageFeeValue(competitorValues);
  }

  if (competitorValues.some(Array.isArray)) {
    return getAverageArrayValue(competitorValues);
  }

  return getAverageValueFromSamples(competitorValues) ?? CMA_SUMMARY_NOT_APPLICABLE_VALUE;
};

const addCompetitorAverageColumn = (rows: any[], filters: Partial<CMASummaryFilters>) => {
  if (filters?.viewOptions?.averageCompetitors === false) {
    return rows;
  }

  return rows.map((row) => {
    const [rowMetadata, communityValue, ...competitorValues] = row;
    return [rowMetadata, communityValue, getCompetitorAverageValue(rowMetadata, competitorValues), ...competitorValues];
  });
};

const addDataDifferenceValues = (rows: any[], filters: Partial<CMASummaryFilters>) => {
  if (filters?.viewOptions?.valueDisplay === VALUE_DIFFERENCE_DISPLAY.DONT_DISPLAY) {
    return rows;
  }

  const calculatePercentageDifference = (a: number, b: number) => {
    if (a === 0) {
      return null;
    }

    const percentage = ((b - a) / a) * 100;
    return Math.round(percentage);
  };

  return rows.map((row: any[]) => {
    const rowMetadata = row[0];
    const communityValues = row[1];
    if (!rowMetadata || !communityValues) {
      return row;
    }

    if (
      rowMetadata.type === CMA_SUMMARY_APARTMENT_TYPE_GROUP_DATA_ROW ||
      rowMetadata.type === CMA_SUMMARY_APARTMENT_TYPE_DATA_ROW ||
      rowMetadata.type === CMA_SUMMARY_LIVING_TYPE_FEE_DATA_ROW ||
      rowMetadata.type === CMA_SUMMARY_CARE_FEE_DATA_ROW ||
      rowMetadata.type === CMA_SUMMARY_CARE_FEE_GROUP_DATA_ROW ||
      rowMetadata.type === CMA_SUMMARY_MEDICATION_FEE_DATA_ROW ||
      rowMetadata.type === CMA_SUMMARY_MEDICATION_FEE_GROUP_DATA_ROW ||
      rowMetadata.type === CMA_SUMMARY_DATA_ROW_TYPE.REVIEWS ||
      rowMetadata.type === CMA_SUMMARY_APARTMENT_TYPE_GROUP_DATA_ROW_ROOM_COMPARISON ||
      rowMetadata.type === CMA_SUMMARY_APARTMENT_TYPE_DATA_ROW_ROOM_COMPARISON
    ) {
      return row.map((rowValues: any, index: number) => {
        if (index < 2) {
          return rowValues; // skip metadata and community data
        }

        const applyDifference = (value: any, valueIndex?: number) => {
          const communityValue = Array.isArray(communityValues) ? communityValues[valueIndex ?? 0] : communityValues;
          const baseValue = getCellNumericValue(communityValue);
          const currentValue = getCellNumericValue(value);

          if (
            baseValue === null ||
            baseValue === undefined ||
            currentValue === null ||
            currentValue === undefined ||
            Number.isNaN(Number(baseValue)) ||
            currentValue === baseValue
          ) {
            return value;
          }

          let differenceValue: number | null | undefined;
          if (filters?.viewOptions?.valueDisplay === VALUE_DIFFERENCE_DISPLAY.AMOUNTS) {
            differenceValue = currentValue - (baseValue as number);
          } else if (filters?.viewOptions?.valueDisplay === VALUE_DIFFERENCE_DISPLAY.PERCENTAGES) {
            differenceValue = calculatePercentageDifference(baseValue as number, currentValue);
          }

          if (differenceValue === null || differenceValue === undefined) {
            return value;
          }

          return setCellDifference(value, differenceValue);
        };

        if (Array.isArray(rowValues)) {
          return rowValues.map((value: any, valueIndex: number) => applyDifference(value, valueIndex));
        }

        return applyDifference(rowValues);
      });
    }

    return row;
  });
};

export const formatAmount = (prefix: string, value?: number | null) => {
  if (typeof value !== 'number' || isNaN(value)) {
    return null;
  }
  return value < 0 ? `-${prefix}${Math.abs(value).toLocaleString()}` : `+${prefix}${value.toLocaleString()}`;
};

export const formatPercentage = (value?: number | null) => {
  if (typeof value !== 'number' || isNaN(value)) {
    return null;
  }
  return value < 0 ? `-${Math.abs(value).toLocaleString()}%` : `+${value.toLocaleString()}%`;
};

export const parseCMASummaryCommunityData = (
  data: any,
  valueDisplay: number | undefined
): CMASummaryParsedCommunityData => {
  const normalizeValue = (value: any): CMASummaryParsedValue => {
    if (value === null || value === undefined) {
      return { value: null };
    }

    if (isValueObject(value)) {
      return {
        value: value.value ?? null,
        difference: value.difference ?? undefined,
        highlight: value.highlight,
      };
    }

    if (typeof value === 'number') {
      return {
        value,
      };
    }

    if (Array.isArray(value)) {
      if (!value.length) {
        return { value: null };
      }
      const primaryValue = getCellNumericValue(value[0]);
      const differenceValue =
        value.length > 1
          ? getCellNumericValue(value[1]) ?? (typeof value[1] === 'number' ? value[1] : undefined)
          : undefined;

      return {
        value: primaryValue ?? null,
        difference: differenceValue ?? undefined,
      };
    }

    return { value: getCellNumericValue(value) };
  };

  if (data === null || data === undefined) {
    return {};
  }

  const rawValues = Array.isArray(data) ? data : [data];

  const parsedValues = rawValues.map((item) => {
    const parsedValue = normalizeValue(item);
    if (valueDisplay === VALUE_DIFFERENCE_DISPLAY.DONT_DISPLAY) {
      return {
        ...parsedValue,
        difference: undefined,
      };
    }
    return parsedValue;
  });

  return {
    dataItemOne: parsedValues[0],
    dataItemTwo: parsedValues[1],
    dataItemThree: parsedValues[2],
    dataItemFour: parsedValues[3],
  };
};

export const getRowKey = ({
  type,
  livingType,
  apartmentType,
  apartmentGroupType,
  roomComparisonFilters,
  contract,
  level,
  index,
}: {
  type: string;
  livingType?: string;
  apartmentGroupType?: string;
  apartmentType?: string;
  roomComparisonFilters?: CMASummaryFilterField[];
  contract?: CommunityContract;
  level?: number;
  index?: number;
}) => {
  if (type === CMA_SUMMARY_APARTMENT_TYPE_GROUP_DATA_ROW_ROOM_COMPARISON) {
    return `${CMA_SUMMARY_APARTMENT_TYPE_GROUP_DATA_ROW_ROOM_COMPARISON}-${livingType}-${contract?.slug}-${apartmentGroupType}-${index}`;
  } else if (type === CMA_SUMMARY_APARTMENT_TYPE_GROUP_DATA_ROW) {
    return `${CMA_SUMMARY_APARTMENT_TYPE_GROUP_DATA_ROW}-${livingType}-${apartmentGroupType}-${contract?.slug}`;
  } else if (type === CMA_SUMMARY_APARTMENT_TYPE_DATA_ROW_ROOM_COMPARISON) {
    return `${CMA_SUMMARY_APARTMENT_TYPE_DATA_ROW_ROOM_COMPARISON}-${livingType}-${apartmentType}-${contract?.slug}-roomComparison-${roomComparisonFilters
      ?.filter((item) => Boolean(item))
      .map((item) => `${item?.name}-${item?.fieldType}`)
      .join('-')}`;
  } else if (type === CMA_SUMMARY_CARE_FEE_DATA_ROW) {
    return `${CMA_SUMMARY_CARE_FEE_DATA_ROW}-${livingType}-${level}-${contract?.slug}`;
  } else if (type === CMA_SUMMARY_CARE_FEE_GROUP_DATA_ROW) {
    return `${CMA_SUMMARY_CARE_FEE_GROUP_DATA_ROW}-${livingType}-${contract?.slug}`;
  } else if (type === CMA_SUMMARY_MEDICATION_FEE_DATA_ROW) {
    return `${CMA_SUMMARY_MEDICATION_FEE_DATA_ROW}-${livingType}-${level}-${contract?.slug}`;
  } else if (type === CMA_SUMMARY_MEDICATION_FEE_GROUP_DATA_ROW) {
    return `${CMA_SUMMARY_MEDICATION_FEE_GROUP_DATA_ROW}-${livingType}-${contract?.slug}`;
  } else if (type === CMA_SUMMARY_COMMUNITY_CONTRACT_ROW) {
    return `${CMA_SUMMARY_COMMUNITY_CONTRACT_ROW}-${livingType}-${contract?.slug}`;
  }
};

export const parseCMASummaryFeeData = (data?: any, valueDisplay?: number): (number | null)[] => {
  const getRoundedNumericValue = (value: any) => {
    const numericValue = extractNumericValue(value);
    return numericValue === null ? null : Math.round(numericValue);
  };

  if (Array.isArray(data) && valueDisplay === VALUE_DIFFERENCE_DISPLAY.DONT_DISPLAY) {
    return [extractNumericValue(data), null];
  }

  if (
    !data ||
    !Array.isArray(data) ||
    typeof data === 'number' ||
    valueDisplay === VALUE_DIFFERENCE_DISPLAY.DONT_DISPLAY
  ) {
    return [extractNumericValue(data), null];
  }

  if (Array.isArray(data[0])) {
    return [extractNumericValue(data[0][0]), getRoundedNumericValue(data[0][1])];
  }
  return [extractNumericValue(data[0]), getRoundedNumericValue(data[1])];
};

export const isViewCMA = (view?: { category?: string }) => {
  return view?.category === VIEW_CATEGORY.CMA_SUMMARY;
};

export const getCMAChildrenRows = (parentRowKey: string, data?: any[]) => {
  if (!data?.length) {
    return [];
  }

  return data.filter((row: any[]) => {
    const rowMetadata = row[0];

    return rowMetadata.parent === parentRowKey;
  });
};

const dataBelongsToContract = (targetContract: CommunityContract, data: any, livingTypeId: number) =>
  data?.livingInfo?.some(
    (item: any) =>
      item.livingTypeId === livingTypeId &&
      item.contracts?.some((c: CommunityContract) => c.slug === targetContract.slug)
  ) ||
  data.fees?.some(
    (item: any) =>
      item.livingTypeId === livingTypeId &&
      item.contracts?.some((c: CommunityContract) => c.slug === targetContract.slug)
  ) ||
  data.feesIncentives?.some(
    (item: any) =>
      item.livingTypeId === livingTypeId &&
      item.contracts?.some((c: CommunityContract) => c.slug === targetContract.slug)
  );

const getContractsFromData = (data: MostCommonContractProps['data'], livingTypeId: number) => {
  const contractsBySlug = new Map<string, CommunityContract>();

  [...(data.livingInfo ?? []), ...(data.fees ?? []), ...(data.feesIncentives ?? [])].forEach((item: any) => {
    if (item?.livingTypeId !== livingTypeId) {
      return;
    }

    item.contracts?.forEach((contract: CommunityContract) => {
      if (contract?.slug && !contractsBySlug.has(contract.slug)) {
        contractsBySlug.set(contract.slug, contract);
      }
    });
  });

  return Array.from(contractsBySlug.values());
};

type MostCommonContractProps = {
  baseContract: CommunityContract;
  competitor: CommunityType;
  selectionType?: number;
  overrideContractId?: number;
  livingTypeId: number;
  data: {
    livingInfo?: any[];
    fees?: any[];
    feesIncentives?: CMASummaryIncentive[];
  };
};
const getMostCommonContract = ({
  baseContract,
  competitor,
  selectionType = COMMUNITY_CONTRACT_SELECTION_TYPE.APPROXIMATE,
  overrideContractId,
  livingTypeId,
  data,
}: MostCommonContractProps) => {
  if (overrideContractId) {
    const override = (competitor?.contracts ?? []).find((c) => c.id === overrideContractId);
    if (override) {
      return override;
    }
  }
  if (selectionType === COMMUNITY_CONTRACT_SELECTION_TYPE.EXACT) {
    if (dataBelongsToContract(baseContract, data, livingTypeId)) {
      return baseContract;
    }
    return null;
  } else if (selectionType === COMMUNITY_CONTRACT_SELECTION_TYPE.APPROXIMATE) {
    const competitorContracts = competitor?.contracts || [];

    const exactMatchContract = competitorContracts.find(
      (c) => c.slug === baseContract.slug && dataBelongsToContract(c, data, livingTypeId)
    );
    const typeMatches = competitorContracts.filter(
      (c) => c.type === baseContract.type && dataBelongsToContract(c, data, livingTypeId)
    );
    const refundMatches = competitorContracts.filter(
      (c) => c.refund_percentage === baseContract.refund_percentage && dataBelongsToContract(c, data, livingTypeId)
    );
    const anyMatchWidthData = competitorContracts.find((c) => dataBelongsToContract(c, data, livingTypeId));
    const dataContracts = getContractsFromData(data, livingTypeId);

    if (exactMatchContract) {
      // 1. Try exact match (both type and refund)
      return exactMatchContract;
    } else if (typeMatches.length) {
      // 2. Try match by contract type only, then closest refund
      return typeMatches.sort((a, b) => {
        const baseRefund = baseContract.refund_percentage;
        return Math.abs(a.refund_percentage - baseRefund) - Math.abs(b.refund_percentage - baseRefund);
      })[0];
    } else if (refundMatches.length) {
      // 3. Try match by refund only (any contract type)
      return refundMatches[0];
    } else if (anyMatchWidthData) {
      // 4. If nothing else, return any contract with data
      return anyMatchWidthData;
    } else if (dataContracts.length) {
      // Public shared views can have thinner competitor metadata after switching the base community.
      return dataContracts[0];
    }
    return null;
  }
};

export const getCommunityContractLabel = (contract: CommunityContract) => {
  const refund = contract.contract_id === COMMUNITY_CONTRACT_TYPE.RENTAL ? undefined : `${contract.refund_percentage}`;
  const refundString = refund ? ` ${refund}%` : '';
  return `${contract.name}${refundString}`;
};
